import { range } from 'lodash';
import { ChangeEvent, ClipboardEvent, createRef, RefObject, useState } from 'react';
import { VisuallyHidden } from 'react-aria';
import Input from 'shared/components/Input';
import { COLORS, SPACINGS } from 'shared/styles/variables';
import styled, { css, keyframes } from 'styled-components';

import { CONFIRMATION_CODE_LENGTH } from 'lib/constants/verification';

const Container = styled('div')`
  display: flex;
  justify-content: space-between;
  position: relative;

  margin-left: -${SPACINGS.base}px;
  margin-right: -${SPACINGS.base}px;
  /* Code input should not be mirrored in RTL languages */
  direction: ltr;
`;

const HiddenInput = styled(Input)`
  background: transparent !important;
  border-color: transparent !important;
  box-shadow: none !important;
  caret-color: transparent;
  color: transparent !important;

  position: absolute;
  left: 0;
  right: 0;
  top: 0;
  bottom: 0;

  &::selection {
    color: transparent;
  }
`;

const blink = keyframes`
  49% {
    color: ${COLORS.gray100};
  }
  50% {
    color: transparent;
  }
  99% {
    color: transparent;
  }
  100% {
    color: ${COLORS.gray100};
  }
`;

const CodeInput = styled(Input)<{ isFocused: boolean; isCursor: boolean }>`
  text-align: center;

  margin-left: ${SPACINGS.base}px;
  margin-right: ${SPACINGS.base}px;

  ${(props) =>
    props.isFocused &&
    css`
      border-color: ${props.theme.colorPrimary};
      color: ${COLORS.gray100};

      // Use box-shadow to ensure that inner content doesn't collapse (e.g.
      // from using border-width.
      box-shadow: 0 0 0 1px ${props.theme.colorPrimary};
    `};

  ${(props) =>
    props.isCursor &&
    css`
      animation-name: ${blink};
      animation-duration: 1s;
      animation-iteration-count: infinite;
    `};
`;

export interface PinInputProps {
  id: string;
  'data-test'?: string;
  allowsLetters?: boolean;
  autoFocus?: boolean;
  codeLength?: number;
  disabled?: boolean;
  handleSubmitCode?: (values: { 'confirmation-code': string }) => void;
  name?: string;
  onChange: (value: string) => void;
  value: string;
  refHiddenInput?: RefObject<HTMLInputElement>;
  hasError?: boolean;
}

const PinInput = ({
  id,
  'data-test': dataTest,
  allowsLetters = false,
  autoFocus = false,
  codeLength = CONFIRMATION_CODE_LENGTH,
  disabled = false,
  name,
  value = '',
  handleSubmitCode = () => undefined,
  onChange,
  refHiddenInput,
  hasError,
}: PinInputProps) => {
  const [isFocused, setIsFocused] = useState<boolean>(autoFocus);

  const sanitizedValue = sanitize(value, { allowsLetters, codeLength });

  refHiddenInput ??= createRef<HTMLInputElement>();

  const keyDown = () => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    refHiddenInput.current.selectionStart = refHiddenInput.current.selectionEnd =
      sanitizedValue.length;
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
  };

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange(sanitize(e.target.value, { allowsLetters, codeLength }));
  };

  const handlePaste = (event: ClipboardEvent<HTMLInputElement>) => {
    const clipboardData = event.clipboardData || (window as any).clipboardData;
    if (!clipboardData) return;
    const clipboardText = sanitize(clipboardData.getData('text'), {
      allowsLetters,
      codeLength,
    });
    // Allow spaces at end
    const isValidCode = new RegExp(`\\d{${codeLength}}`).test(clipboardText);

    if (isValidCode) {
      onChange(clipboardText);
      handleSubmitCode({ 'confirmation-code': clipboardText });
    }
  };

  return (
    <Container data-test={dataTest}>
      <VisuallyHidden>
        <label htmlFor={id}>{`${codeLength} digit code`}</label>
      </VisuallyHidden>
      <HiddenInput
        id={id}
        autoComplete='no-autocomplete'
        autoFocus={autoFocus}
        name={name}
        ref={refHiddenInput}
        value={sanitizedValue}
        onBlur={handleBlur}
        onChange={handleChange}
        onFocus={handleFocus}
        onKeyDown={keyDown}
        onPaste={handlePaste}
        aria-invalid={hasError}
        aria-errormessage={`${id}--error`}
        aria-describedby={`${id}--error`}
      />
      {range(codeLength).map((index) => {
        const isSelected = isFocused && Math.min(sanitizedValue.length, codeLength - 1) === index;

        const currentValue = sanitizedValue.charAt(index);
        const valueToDisplay = currentValue !== '' ? currentValue : isSelected ? '|' : '';

        return (
          <CodeInput
            disabled={disabled}
            isCursor={currentValue === ''}
            isFocused={isSelected}
            key={index}
            maxLength={1}
            onChange={(): void => undefined}
            tabIndex={-1}
            type='tel'
            value={valueToDisplay}
          />
        );
      })}
    </Container>
  );
};

export default PinInput;

function sanitize(
  str: string,
  { allowsLetters, codeLength }: { allowsLetters: boolean; codeLength: number },
): string {
  return (
    str
      .trim()
      .toUpperCase()
      .replace(allowsLetters ? /[^0-9０-９A-Z]/g : /[^0-9０-９]/g, '')
      // NB: Don't make into character group, unicode matching doesn't work
      .replace(/[０-９]/g, (d: string) => (d.charCodeAt(0) - '０'.charCodeAt(0)).toString())
      .substring(0, codeLength)
  );
}
